import { Grid, Stack, Typography } from '@mui/material';
import useVisitorDetailsAccessOverview from 'hooks/allVisitors/pages/userDetails/useVisitorDetailsAccessOverview';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { getLocksThatHaveLockGroupAssigned, getLocksThatHaveVisitorGroupAssigned } from 'utils/Lock/Lock';
import {
  getLockGroupsThatHaveLockAssigned,
  getLockGroupsThatHaveVisitorGroupAssigned
} from 'utils/LockGroup/LockGroup';
import {
  getVisitorGroupsThatHaveLockAssigned,
  getVisitorGroupsThatHaveLockGroupAssigned
} from 'utils/VisitorGroup/VisitorGroup';
import SectionPaper from 'v2/components/SectionPaper/SectionPaper';
import SingleColumnDataGrid from 'v2/components/SingleColumnDataGrid/SingleColumnDataGrid';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';

interface Props {
  visitorId: string;
}

const AccessOverviewTab: React.FC<Props> = ({ visitorId }) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });

  const {
    visitorDetailsVisitorGroupsContainingVisitor,
    loadingGetVisitorDetailsVisitorGroups,
    visitorDetailsLockGroups,
    loadingGetVisitorDetailsLockGroups,
    visitorDetailsDoors,
    loadingGetVisitorDetailsDoors
  } = useVisitorDetailsAccessOverview({ translation, visitorId, handleFetchError });

  const isLoading =
    loadingGetVisitorDetailsVisitorGroups || loadingGetVisitorDetailsLockGroups || loadingGetVisitorDetailsDoors;

  const [selectedVisitorGroup, setSelectedVisitorGroup] = useState<string>();
  const [selectedLockGroup, setSelectedLockGroup] = useState<string>();
  const [selectedLock, setSelectedLock] = useState<string>();

  const handleClickVisitorGroupRow = (visitorGroupId: string) => {
    setSelectedVisitorGroup((current) => (current === visitorGroupId ? undefined : visitorGroupId));
    setSelectedLockGroup(undefined);
    setSelectedLock(undefined);
  };

  const handleClickLockGroupRow = (lockGroupId: string) => {
    setSelectedLockGroup((current) => (current === lockGroupId ? undefined : lockGroupId));
    setSelectedVisitorGroup(undefined);
    setSelectedLock(undefined);
  };

  const handleClickLockRow = (lockId: string) => {
    setSelectedLock((current) => (current === lockId ? undefined : lockId));
    setSelectedVisitorGroup(undefined);
    setSelectedLockGroup(undefined);
  };

  const isVisitorGroupHighlighted = (visitorGroupId: string) => {
    if (selectedVisitorGroup === visitorGroupId) return true;
    if (
      selectedLockGroup !== undefined &&
      visitorDetailsVisitorGroupsContainingVisitor &&
      getVisitorGroupsThatHaveLockGroupAssigned(visitorDetailsVisitorGroupsContainingVisitor, selectedLockGroup).some(
        (visitorGroup) => visitorGroup.id === visitorGroupId
      )
    )
      return true;
    if (
      selectedLock !== undefined &&
      visitorDetailsVisitorGroupsContainingVisitor &&
      getVisitorGroupsThatHaveLockAssigned(visitorDetailsVisitorGroupsContainingVisitor, selectedLock).some(
        (visitorGroup) => visitorGroup.id === visitorGroupId
      )
    )
      return true;
    return false;
  };

  const isLockGroupHighlighted = (lockGroupId: string) => {
    if (selectedLockGroup === lockGroupId) return true;
    if (
      selectedVisitorGroup !== undefined &&
      visitorDetailsVisitorGroupsContainingVisitor &&
      visitorDetailsLockGroups &&
      getLockGroupsThatHaveVisitorGroupAssigned(
        visitorDetailsVisitorGroupsContainingVisitor,
        selectedVisitorGroup
      ).some((lockGroup) => lockGroup.id === lockGroupId)
    )
      return true;
    if (
      selectedLock !== undefined &&
      visitorDetailsLockGroups &&
      getLockGroupsThatHaveLockAssigned(visitorDetailsLockGroups, selectedLock).some(
        (lockGroup) => lockGroup.id === lockGroupId
      )
    )
      return true;
    return false;
  };

  const isLockHighlighted = (lockId: string) => {
    if (selectedLock === lockId) return true;
    if (
      selectedVisitorGroup !== undefined &&
      visitorDetailsVisitorGroupsContainingVisitor &&
      visitorDetailsLockGroups &&
      getLocksThatHaveVisitorGroupAssigned(visitorDetailsVisitorGroupsContainingVisitor, selectedVisitorGroup).some(
        (lock) => lock.id === lockId
      )
    )
      return true;
    if (
      selectedLockGroup !== undefined &&
      visitorDetailsLockGroups &&
      getLocksThatHaveLockGroupAssigned(visitorDetailsLockGroups, selectedLockGroup).some((lock) => lock.id === lockId)
    )
      return true;
    return false;
  };

  return (
    <SectionPaper>
      <Stack spacing={2}>
        <Typography variant="body2" color="textSecondary">
          {translation.user_access_path_hint}
        </Typography>
        <Grid container direction="row" wrap="nowrap" spacing={2} size={12}>
          <Grid container size={4} direction="column">
            <SingleColumnDataGrid
              rows={(visitorDetailsVisitorGroupsContainingVisitor ?? []).map((visitorGroup) => ({
                id: visitorGroup.id,
                name: visitorGroup.name
              }))}
              headerName={translation.user_groups}
              loading={isLoading}
              isHighlighted={isVisitorGroupHighlighted}
              onRowClick={handleClickVisitorGroupRow}
              idPrefix="user-groups-"
              noRowsMessage={translation.no_user_group_assigned}
            />
          </Grid>
          <Grid container size={4} direction="column">
            <SingleColumnDataGrid
              rows={(visitorDetailsLockGroups ?? []).map((lockGroup) => ({ id: lockGroup.id, name: lockGroup.name }))}
              headerName={translation.device_groups}
              loading={isLoading}
              isHighlighted={isLockGroupHighlighted}
              onRowClick={handleClickLockGroupRow}
              idPrefix="device-groups-"
              noRowsMessage={translation.no_device_group_for_user_group}
            />
          </Grid>
          <Grid container size={4} direction="column">
            <SingleColumnDataGrid
              rows={(visitorDetailsDoors ?? []).map((door) => ({ id: door.id, name: door.name }))}
              headerName={translation.devices}
              loading={isLoading}
              isHighlighted={isLockHighlighted}
              onRowClick={handleClickLockRow}
              idPrefix="locks-"
              noRowsMessage={translation.no_device_for_user_and_device_groups}
            />
          </Grid>
        </Grid>
      </Stack>
    </SectionPaper>
  );
};

export default AccessOverviewTab;
